/**
 * Market queries — `/cached/markets_by_ticker/{ticker}` (and friends).
 *
 * React Query wrapper around the market read endpoints. The query key is the
 * ticker, which means N consumers asking for the same market on the same page
 * (e.g. dozens of `<ActivityItem>` rows in the live activity feed sharing a
 * ticker) collapse to a single in-flight request.
 */

import { isServer, queryOptions, useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';

import { createLazyApiClient } from 'lib/api/createLazyApiClient';
import type { MarketVo } from 'models';
import type { GetMarketResponse } from 'models/reqres';
import config from 'utils/config';

const api = createLazyApiClient(() => ({ baseURL: `${config.exchangeApiUrl}/v1` }));

const STALE_TIME_MS = 60 * 1000;
const GC_TIME_MS = 5 * 60 * 1000;

export const marketKeys = {
    all: ['market'] as const,
    byTicker: (ticker: string) => [...marketKeys.all, 'byTicker', ticker] as const,
};

export async function fetchMarketByTicker(ticker: string, signal?: AbortSignal): Promise<MarketVo | null> {
    const response = await api.get<GetMarketResponse>(`/cached/markets_by_ticker/${ticker}`, { signal });
    return response.data.market ?? null;
}

type MarketByTickerOptions = Partial<
    Omit<
        UseQueryOptions<MarketVo | null, Error, MarketVo | null, ReturnType<typeof marketKeys.byTicker>>,
        'queryKey' | 'queryFn'
    >
>;

export function marketByTickerOptions(ticker: string | null | undefined) {
    return queryOptions({
        queryKey: marketKeys.byTicker(ticker ?? ''),
        queryFn: ({ signal }) => fetchMarketByTicker(ticker as string, signal),
        enabled: !!ticker,
        staleTime: STALE_TIME_MS,
        gcTime: isServer ? Infinity : GC_TIME_MS,
        retry: 2,
    });
}

export function useMarketByTickerQuery(
    ticker: string | null | undefined,
    options?: MarketByTickerOptions,
): UseQueryResult<MarketVo | null, Error> {
    return useQuery({ ...marketByTickerOptions(ticker), ...options });
}
